import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { sx } from '../pages/reportShared'

// "My Time" tab — a PM's own weekly timesheet (pm / owner / admin / road_super).
// Reads and writes through the session-authed /api/timesheets endpoint; the
// server owns every identity + permission decision, this file only renders what
// it is handed (`submitted`, `can_oversee`, …) and never infers them from role.
//
// A PM's week has TWO kinds of rows:
//   • field-report rows — READ-ONLY here. The foreman already put him on a
//     daily field report; those hours reach payroll through the report's own
//     approval, so editing them here would fork the truth. Shown for context
//     and counted in the totals.
//   • his own entries — jobsite time the report doesn't cover, office time
//     (job ADMIN), job-cost work. These are what this page writes.

// MIRRORED SERVER COPY: api/lib/office-job.js. Keep the two identical.
const OFFICE_JOB = { number: 'ADMIN', name: 'ADMIN' }

// ── week math (UTC parse, no timezone drift) — same rules as
// api/payroll/csv-builder.js and FieldReportsView. week_start is the MONDAY
// everywhere in code; humans see the week-ENDING Sunday.
function shiftDays(ymd, n) {
  const [y, m, d] = ymd.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d))
  t.setUTCDate(t.getUTCDate() + n)
  return t.toISOString().slice(0, 10)
}
function mondayOf(ymd) {
  const [y, m, d] = ymd.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d))
  t.setUTCDate(t.getUTCDate() - ((t.getUTCDay() + 6) % 7))
  return t.toISOString().slice(0, 10)
}
function todayET() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
}
function dayLabel(ymd) {
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    weekday: 'long', month: 'short', day: 'numeric', timeZone: 'UTC',
  })
}
function weekdayName(ymd) {
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' })
}
function niceDate(ymd) {
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  })
}
function fmtWhen(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100

let rowSeq = 0
const newRow = (workDate) => ({
  _k: `r${++rowSeq}`,
  work_date: workDate,
  job_number: OFFICE_JOB.number,
  job_name: OFFICE_JOB.name,
  cost_code: '',
  pay_category: '',   // '' = Auto: REG/OVT/DBL by day of week, as before
  hours: '',
  note: '',
})

// No `role` prop on purpose: every capability on this screen (can_oversee, the
// submitted lock) is decided SERVER-side and read off the payload, so the client
// has nothing to infer from a role.
export default function MyTimeView({ session }) {
  const [weekStart, setWeekStart] = useState(() => mondayOf(todayET()))
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [data, setData] = useState(null)      // server payload for the week
  const [rows, setRows] = useState([])        // editable copy of `entries`
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState(null)        // { kind, text }
  const [overview, setOverview] = useState(null)
  // Wrong-week guard state: weekNote is a sticky banner tied to ONE week (it
  // re-appears whenever that week is shown, and hides on any other week —
  // unlike `msg`, load() never clears it). autoJumpDone makes the Monday
  // auto-jump a one-shot per mount.
  const [weekNote, setWeekNote] = useState(null)   // { week, text }
  const autoJumpDone = useRef(false)

  const token = useCallback(async () => (
    session?.access_token || (await supabase.auth.getSession()).data?.session?.access_token
  ), [session])

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => shiftDays(weekStart, i)), [weekStart])
  const weekEnd = days[6]
  const submitted = !!(data && data.submitted)
  const canOversee = !!(data && data.can_oversee)
  const locked = submitted

  const load = useCallback(async () => {
    setLoading(true); setErr(null); setMsg(null)
    try {
      const r = await fetch(`/api/timesheets?action=week&week_start=${weekStart}`, {
        headers: { Authorization: `Bearer ${await token()}` },
      })
      const j = await r.json()
      if (!r.ok || !j.ok) { setErr(j.error || 'db_error'); setData(null); setRows([]); return }
      setData(j)
      setRows((j.entries || []).map(e => ({
        _k: `r${++rowSeq}`,
        work_date: e.work_date,
        job_number: e.job_number || '',
        job_name: e.job_name || '',
        cost_code: e.cost_code || '',
        pay_category: e.pay_category || '',
        hours: e.hours == null ? '' : String(e.hours),
        note: e.note || '',
      })))
      setDirty(false)
    } catch (e) {
      setErr('db_error')
    } finally {
      setLoading(false)
    }
  }, [weekStart, token])

  useEffect(() => { load() }, [load])

  // Wrong-week guard #1 (added 2026-08-10, after 40h landed on the week ending
  // 8/16 while payroll wanted 8/9): this page used to always open on the
  // CURRENT week, which on payroll Monday is exactly the wrong one for a PM
  // entering last week's hours. On Monday and Tuesday, if LAST week has not
  // been submitted, open there instead — with a banner saying so. "This week"
  // is always one click away, and the jump never repeats within a mount.
  useEffect(() => {
    if (autoJumpDone.current) return
    autoJumpDone.current = true
    const today = todayET()
    const thisMon = mondayOf(today)
    if (today !== thisMon && today !== shiftDays(thisMon, 1)) return
    ;(async () => {
      try {
        const prev = shiftDays(thisMon, -7)
        const r = await fetch(`/api/timesheets?action=week&week_start=${prev}`, {
          headers: { Authorization: `Bearer ${await token()}` },
        })
        const j = await r.json()
        if (r.ok && j.ok && !j.submitted) {
          setWeekStart(prev)
          setWeekNote({
            week: prev,
            text: `Heads up — last week hasn't been submitted, so you're looking at the week ending ${niceDate(shiftDays(prev, 6))}. Monday's payroll export runs on these hours. Use "This week" to jump forward.`,
          })
        }
      } catch (e) { /* couldn't check — stay on the current week */ }
    })()
  }, [token])

  // Status strip for owner/admin/road_super — who has submitted this week.
  const loadOverview = useCallback(async () => {
    if (!canOversee) { setOverview(null); return }
    try {
      const r = await fetch(`/api/timesheets?action=overview&week_start=${weekStart}`, {
        headers: { Authorization: `Bearer ${await token()}` },
      })
      const j = await r.json()
      setOverview(r.ok && j.ok ? j.people || [] : null)
    } catch (e) { setOverview(null) }
  }, [canOversee, weekStart, token])

  useEffect(() => { loadOverview() }, [loadOverview])

  // ── totals (self-entered + field-report rows, so the week total is the
  // PM's ACTUAL week, not just what he typed) ────────────────────────────────
  const reportRowsByDay = useMemo(() => {
    const m = {}
    for (const r of (data && data.report_rows) || []) {
      if (!m[r.work_date]) m[r.work_date] = []
      m[r.work_date].push(r)
    }
    return m
  }, [data])

  const dayTotal = useCallback((d) => {
    const mine = rows.filter(r => r.work_date === d).reduce((s, r) => s + (Number(r.hours) || 0), 0)
    const rep = (reportRowsByDay[d] || []).reduce((s, r) => s + (Number(r.hours) || 0), 0)
    return round2(mine + rep)
  }, [rows, reportRowsByDay])

  const weekTotal = useMemo(() => round2(days.reduce((s, d) => s + dayTotal(d), 0)), [days, dayTotal])
  const selfTotal = useMemo(() => round2(rows.reduce((s, r) => s + (Number(r.hours) || 0), 0)), [rows])

  // ── row editing ─────────────────────────────────────────────────────────────
  function patchRow(k, patch) {
    setRows(prev => prev.map(r => (r._k === k ? { ...r, ...patch } : r)))
    setDirty(true); setMsg(null)
  }
  function addRow(d) { setRows(prev => [...prev, newRow(d)]); setDirty(true); setMsg(null) }
  function delRow(k) { setRows(prev => prev.filter(r => r._k !== k)); setDirty(true); setMsg(null) }

  function pickJob(k, num) {
    const job = (data.jobs || []).find(j => j.num === num)
    patchRow(k, {
      job_number: num,
      job_name: num === OFFICE_JOB.number ? OFFICE_JOB.name : (job ? job.name : ''),
      // The old code almost certainly isn't on the new job — clear it rather
      // than carry a code that job doesn't have.
      cost_code: '',
    })
  }

  // ADMIN's cost codes come from the SERVER (office_cost_codes), not a copy in
  // this file — that job has no rows in the cost_codes table, so the server's
  // constant is the only authority and serving it keeps the picker and the
  // server's validation from drifting.
  const officeCodes = useMemo(() => (data && data.office_cost_codes) || [], [data])
  const codesFor = (num) => (
    num === OFFICE_JOB.number ? officeCodes : ((data && data.cost_codes && data.cost_codes[num]) || [])
  )

  // ── pay categories (REG / OVT / DBL / VAC / HOL) ────────────────────────────
  // Served by the API (pay_categories) — never a copy in this file, so the
  // picker and the server's validation cannot drift. '' on a row = AUTO: the
  // day-of-week rule decides, exactly as before this dropdown existed.
  const payCats = useMemo(() => (data && data.pay_categories) || [], [data])
  const catFor = (code) => payCats.find(c => c.code === String(code || '').trim().toUpperCase()) || null
  // Paid-not-worked time (VAC / HOL): no job, no cost code. The server forces
  // ADMIN + a null code on these rows regardless; the UI mirrors that so what
  // the PM sees is what gets saved.
  const isNonWorked = (r) => { const c = catFor(r.pay_category); return !!c && c.requires_cost_code === false }
  // What AUTO resolves to on a given day — served per date by the API from the
  // csv-builder rule, so there is no client-side copy of that rule.
  const autoRateFor = (ymd) => ((data && data.auto_rate_codes) || {})[ymd] || ''
  function pickCategory(k, code) {
    const c = catFor(code)
    if (c && c.requires_cost_code === false) {
      patchRow(k, { pay_category: c.code, job_number: OFFICE_JOB.number, job_name: OFFICE_JOB.name, cost_code: '' })
    } else {
      // Back to a worked category (or auto): the job + cost-code selects
      // re-enable and whatever they hold is validated as usual.
      patchRow(k, { pay_category: c ? c.code : '' })
    }
  }
  // Hours in a paid-not-worked category — shown as a sub-line under the week
  // total so a 40h week reads as "40 h … incl. 8 h vacation/holiday".
  const nonWorkedTotal = useMemo(
    () => round2(rows.reduce((s, r) => s + (isNonWorked(r) ? (Number(r.hours) || 0) : 0), 0)),
    [rows, payCats]
  )

  // A job with NO synced cost codes cannot be completed at all — there is no
  // free-text path any more, so the row is a dead end until Carla adds the
  // codes in Plexxis. Reported separately from rowProblem so the row can render
  // its own explanation instead of a terse validation chip.
  const noCodesFor = (num) => !!num && codesFor(num).length === 0

  // The options for one row: the job's codes, plus — if the row already holds a
  // code that is NO LONGER in that list — the stored code itself, marked.
  // Without this a Plexxis renumber would leave a <select> whose value matches
  // no <option>, which renders BLANK: the code would vanish from the screen
  // while still sitting in the row's state, and the PM would have no idea what
  // he was about to overwrite.
  function optionsFor(r) {
    const list = codesFor(r.job_number)
    const cur = String(r.cost_code || '').trim()
    if (!cur || !list.length || list.some(c => c.code === cur)) return list
    return [...list, { code: cur, description: 'no longer in Plexxis', _stale: true }]
  }

  // Client-side mirror of the server's rules: a job, a cost code, and hours on
  // a quarter (0.25–24). Checked in the order the PM fills the row in.
  //
  // Membership is NOT re-checked here — the control is a <select>, so anything
  // the PM can pick is a real option by construction. Flagging a stale stored
  // code instead would disable Submit and strand a finished week, the very
  // thing the server's submit rule is written to avoid.
  function hoursProblem(r) {
    const h = Number(r.hours)
    if (r.hours === '' || !Number.isFinite(h)) return 'Enter hours'
    if (h < 0.25) return 'Min 0.25'
    if (h > 24) return 'Max 24'
    if (Math.round(h * 4) !== h * 4) return 'Use 0.25 steps'
    return null
  }
  function rowProblem(r) {
    // Vacation / holiday: there is no job and no cost code to complain about —
    // only the hours matter (the server skips the cost-code rules for these too).
    if (isNonWorked(r)) return hoursProblem(r)
    if (!r.job_number) return 'Pick a job'
    if (noCodesFor(r.job_number)) return 'No cost codes for this job'
    if (!String(r.cost_code || '').trim()) return 'Pick a cost code'
    return hoursProblem(r)
  }
  const firstProblem = useMemo(() => {
    for (const r of rows) { const p = rowProblem(r); if (p) return `${dayLabel(r.work_date)}: ${p}` }
    return null
  }, [rows, officeCodes, payCats])

  // ── actions ─────────────────────────────────────────────────────────────────
  async function post(action, body) {
    const r = await fetch(`/api/timesheets?action=${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await token()}` },
      body: JSON.stringify(body),
    })
    const j = await r.json().catch(() => ({}))
    return { ok: r.ok && j.ok, j }
  }

  async function handleSave(silent) {
    if (firstProblem) { setMsg({ kind: 'err', text: firstProblem }); return false }
    setBusy(true)
    const { ok, j } = await post('save', {
      week_start: weekStart,
      entries: rows.map(r => ({
        work_date: r.work_date, job_number: r.job_number, job_name: r.job_name,
        cost_code: r.cost_code, pay_category: r.pay_category || null,
        hours: Number(r.hours), note: r.note,
      })),
    })
    setBusy(false)
    if (!ok) { setMsg({ kind: 'err', text: j.detail || j.error || 'Could not save.' }); return false }
    setDirty(false)
    if (!silent) setMsg({ kind: 'ok', text: 'Saved.' })
    return true
  }

  async function handleSubmit() {
    if (firstProblem) { setMsg({ kind: 'err', text: firstProblem }); return }
    // Wrong-week guard #2: hours dated after today usually mean the PM typed
    // LAST week's time into the current week (the picker used to open there).
    // Same single confirm either way — just a much louder question.
    const today = todayET()
    const futureH = round2(rows.reduce((s, r) => s + (r.work_date > today ? (Number(r.hours) || 0) : 0), 0))
    const ok = window.confirm(
      futureH > 0
        ? `⚠ Hold on — ${futureH} of the ${selfTotal} hours on this timesheet are dated AFTER today.\n\n` +
          `You're submitting the week ending ${niceDate(weekEnd)}. If you meant to enter LAST week's hours ` +
          `(week ending ${niceDate(shiftDays(weekStart, -1))}), press Cancel, use ← Prev to switch weeks, ` +
          `and enter them there.\n\n` +
          `Submit the week ending ${niceDate(weekEnd)} anyway?`
        : `Submit your timesheet for the week ending ${niceDate(weekEnd)}?\n\n` +
          `${selfTotal} hours of your own entries will go to payroll. ` +
          `Once submitted the week is locked — an owner or the office can unlock it if something needs fixing.`
    )
    if (!ok) return
    // Save first so a submit can never lock in a stale set of rows.
    if (dirty && !(await handleSave(true))) return
    setBusy(true)
    const { ok: sok, j } = await post('submit', { week_start: weekStart })
    setBusy(false)
    if (!sok) { setMsg({ kind: 'err', text: j.detail || j.error || 'Could not submit.' }); return }
    setMsg({ kind: 'ok', text: j.already ? 'This week was already submitted.' : 'Timesheet submitted.' })
    load(); loadOverview()
  }

  async function handleUnlock(employeeId, who) {
    if (!window.confirm(`Unlock ${who}'s timesheet for the week ending ${niceDate(weekEnd)}?`)) return
    setBusy(true)
    const { ok, j } = await post('unlock', { week_start: weekStart, employee_id: employeeId })
    setBusy(false)
    if (!ok) { setMsg({ kind: 'err', text: j.detail || j.error || 'Could not unlock.' }); return }
    setMsg({ kind: 'ok', text: `${who}'s week is unlocked.` })
    loadOverview()
    if (data && employeeId === data.employee_id) load()
  }

  async function handleCopyLastWeek() {
    const prev = shiftDays(weekStart, -7)
    setBusy(true)
    try {
      const r = await fetch(`/api/timesheets?action=week&week_start=${prev}`, {
        headers: { Authorization: `Bearer ${await token()}` },
      })
      const j = await r.json()
      if (!r.ok || !j.ok) { setMsg({ kind: 'err', text: 'Could not read last week.' }); return }
      // Self-entered rows ONLY. Field-report rows belong to their reports and
      // are never copied forward.
      const copied = (j.entries || []).map(e => ({
        _k: `r${++rowSeq}`,
        work_date: shiftDays(e.work_date, 7),
        job_number: e.job_number || '',
        job_name: e.job_name || '',
        cost_code: e.cost_code || '',
        pay_category: e.pay_category || '',
        hours: e.hours == null ? '' : String(e.hours),
        note: e.note || '',
      }))
      if (!copied.length) { setMsg({ kind: 'warn', text: 'Last week had no entries to copy.' }); return }
      setRows(prev2 => [...prev2, ...copied])
      setDirty(true)
      setMsg({ kind: 'warn', text: `Copied ${copied.length} row${copied.length === 1 ? '' : 's'} from last week — review, then Save.` })
    } finally {
      setBusy(false)
    }
  }

  // ── render ──────────────────────────────────────────────────────────────────
  if (loading) return <div style={{ padding: 20, color: '#666', fontSize: 13 }}>Loading your timesheet…</div>

  if (err === 'no_employee_link') {
    return (
      <div style={{ padding: 20, maxWidth: 640 }}>
        <div style={sx.banner('#fff8f0', '#e8820c', '#92510a')}>
          <strong>Your login isn’t linked to an employee record yet.</strong>
          <div style={{ marginTop: 6 }}>
            A timesheet has to attach to a payroll employee number, so we can’t open this page until
            the link exists. Ask Patrick to link your login on the <strong>Users</strong> tab — it takes a moment,
            and everything here will work straight afterwards.
          </div>
        </div>
      </div>
    )
  }
  if (err) {
    return (
      <div style={{ padding: 20, maxWidth: 640 }}>
        <div style={sx.banner('#fdecec', '#d9534f', '#a12622')}>
          Couldn’t load your timesheet ({err}). Try again in a moment.
        </div>
      </div>
    )
  }

  const msgStyle = msg
    ? (msg.kind === 'ok' ? sx.banner('#eaf6ec', '#1e7e34', '#1e5128')
      : msg.kind === 'warn' ? sx.banner('#fff8e1', '#e0a800', '#8a5d00')
        : sx.banner('#fdecec', '#d9534f', '#a12622'))
    : null

  return (
    <div style={{ padding: 16, maxWidth: 1100 }}>
      {/* Week header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <button className="btn btn-sm" disabled={busy} onClick={() => setWeekStart(shiftDays(weekStart, -7))}>← Prev</button>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1e2d3d' }}>Week ending {niceDate(weekEnd)}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{niceDate(weekStart)} → {niceDate(weekEnd)}</div>
        </div>
        <button className="btn btn-sm" disabled={busy} onClick={() => setWeekStart(shiftDays(weekStart, 7))}>Next →</button>
        <button className="btn btn-sm" disabled={busy} onClick={() => setWeekStart(mondayOf(todayET()))}>This week</button>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#1e7e34' }}>{weekTotal} h</div>
          <div style={{ fontSize: 11, color: '#888' }}>week total (incl. field reports)</div>
          {nonWorkedTotal > 0 && (
            <div style={{ fontSize: 11, color: '#888' }}>incl. {nonWorkedTotal} h vacation/holiday</div>
          )}
        </div>
      </div>

      {msg && <div style={{ ...msgStyle, marginBottom: 12 }}>{msg.text}</div>}
      {weekNote && weekNote.week === weekStart && (
        <div style={{ ...sx.banner('#fff8e1', '#e0a800', '#8a5d00'), marginBottom: 12 }}>{weekNote.text}</div>
      )}

      {submitted && (
        <div style={{ ...sx.banner('#eaf6ec', '#1e7e34', '#1e5128'), marginBottom: 12 }}>
          ✅ <strong>Submitted</strong>{data.week?.submitted_at ? ` ${fmtWhen(data.week.submitted_at)}` : ''}
          {data.week?.submitted_by_email ? ` by ${data.week.submitted_by_email}` : ''}. These hours go into the
          Monday payroll export. Need a change? Ask an owner or the office to unlock the week.
        </div>
      )}

      {!submitted && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <button className="btn btn-sm" disabled={busy} onClick={handleCopyLastWeek}>⧉ Copy last week</button>
          <button className="btn btn-sm" disabled={busy || !dirty || !!firstProblem} onClick={() => handleSave(false)}>
            {busy ? 'Working…' : dirty ? 'Save' : 'Saved'}
          </button>
          <button
            className="btn btn-sm btn-primary"
            disabled={busy || rows.length === 0 || !!firstProblem}
            onClick={handleSubmit}
          >
            Submit week
          </button>
          {firstProblem && <span style={{ fontSize: 12, color: '#a12622', alignSelf: 'center' }}>{firstProblem}</span>}
        </div>
      )}

      {/* Days */}
      {days.map(d => {
        const mine = rows.filter(r => r.work_date === d)
        const reps = reportRowsByDay[d] || []
        const isToday = d === todayET()
        return (
          <div key={d} style={{
            border: '1px solid #e3e7ec', borderRadius: 8, marginBottom: 10, overflow: 'hidden',
            background: '#fff',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
              background: isToday ? '#eef5ff' : '#f7f9fb', borderBottom: '1px solid #e3e7ec',
            }}>
              <strong style={{ fontSize: 13, color: '#1e2d3d' }}>{dayLabel(d)}</strong>
              {isToday && <span style={{ fontSize: 11, color: '#2563eb', fontWeight: 600 }}>today</span>}
              <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 700, color: dayTotal(d) ? '#1e2d3d' : '#bbb' }}>
                {dayTotal(d)} h
              </span>
            </div>

            <div style={{ padding: '8px 12px' }}>
              {/* Read-only field-report rows */}
              {reps.map((r, i) => {
                const approved = r.pm_approval_status === 'approved'
                return (
                  <div key={`rep${i}`} style={{
                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                    padding: '6px 8px', marginBottom: 6, borderRadius: 6,
                    background: '#f4f7fa', border: '1px dashed #c9d4de', fontSize: 12.5, color: '#43525f',
                  }}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
                      background: '#e2eaf1', color: '#3d5468',
                    }}>
                      From field report{r.job_number ? ` — Job ${r.job_number}` : ''}
                    </span>
                    <span>{r.job_name || ''}</span>
                    {r.cost_code && <span style={{ color: '#666' }}>cost code {r.cost_code}</span>}
                    <span style={{ fontWeight: 700, color: '#1e2d3d' }}>{round2(r.hours)} h</span>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
                      background: approved ? '#eaf6ec' : '#fff8e1',
                      color: approved ? '#1e5128' : '#8a5d00',
                    }}>
                      {approved ? 'Approved' : 'Pending'}
                    </span>
                    {!approved && (
                      <span style={{ fontSize: 11, color: '#8a5d00' }}>
                        these hours reach payroll only once the report is approved
                      </span>
                    )}
                  </div>
                )
              })}

              {/* The PM's own rows */}
              {mine.map(r => {
                const isOffice = r.job_number === OFFICE_JOB.number
                const nonWorked = isNonWorked(r)
                const codes = optionsFor(r)
                const noCodes = noCodesFor(r.job_number)
                const prob = rowProblem(r)
                const autoCode = autoRateFor(r.work_date)
                return (
                  <div key={r._k} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                    <select
                      value={r.job_number} disabled={locked || nonWorked}
                      onChange={e => pickJob(r._k, e.target.value)}
                      style={{ ...sx.input, width: 230, flex: '0 0 230px' }}
                    >
                      <option value={OFFICE_JOB.number}>{OFFICE_JOB.name} (office / overhead)</option>
                      {(data.jobs || []).map(j => (
                        <option key={j.num} value={j.num}>{j.num} — {j.name}</option>
                      ))}
                    </select>

                    {/* ALWAYS a dropdown — there is no typed-in cost code
                        anywhere on this screen. ADMIN draws its options from
                        the server's OFFICE_COST_CODES, a real job from its own
                        synced codes. A job with NO codes offers nothing to
                        pick and is blocked below rather than falling back to
                        free text. */}
                    <select
                      value={r.cost_code} disabled={locked || noCodes || nonWorked}
                      onChange={e => patchRow(r._k, { cost_code: e.target.value })}
                      style={{
                        ...sx.input, width: 190, flex: '0 0 190px',
                        ...(noCodes || nonWorked ? { background: '#f4f5f7', color: '#999' } : null),
                        ...(!noCodes && !nonWorked && !String(r.cost_code || '').trim() ? { borderColor: '#d9534f' } : null),
                      }}
                    >
                      <option value="">
                        {nonWorked ? 'No cost code needed' : noCodes ? 'No cost codes' : isOffice ? 'Office cost code…' : 'Cost code…'}
                      </option>
                      {codes.map(c => (
                        <option key={c.code} value={c.code}>
                          {c.code}{c.description ? ` — ${c.description}` : ''}
                        </option>
                      ))}
                    </select>

                    {/* Pay category — ALWAYS a dropdown, options served by the
                        API. "" = Auto: the day-of-week rule (REG/OVT/DBL)
                        decides exactly as before this control existed, and
                        the label says what auto resolves to for THIS row's
                        date. Picking VAC / HOL forces the job to ADMIN and
                        clears the cost code (the server does the same). */}
                    <select
                      value={r.pay_category || ''} disabled={locked}
                      onChange={e => pickCategory(r._k, e.target.value)}
                      title="Pay category"
                      style={{ ...sx.input, width: 150, flex: '0 0 150px' }}
                    >
                      <option value="">
                        {autoCode ? `Auto (${autoCode} — ${weekdayName(r.work_date)})` : 'Auto — REG/OVT/DBL'}
                      </option>
                      {payCats.map(c => (
                        <option key={c.code} value={c.code}>
                          {c.code}{c.description ? ` — ${c.description}` : ''}
                        </option>
                      ))}
                    </select>

                    <input
                      type="number" step="0.25" min="0.25" max="24"
                      value={r.hours} disabled={locked || noCodes}
                      placeholder="Hrs"
                      onChange={e => patchRow(r._k, { hours: e.target.value })}
                      style={{
                        ...sx.input, width: 80, flex: '0 0 80px', textAlign: 'center',
                        ...(noCodes ? { background: '#f4f5f7', color: '#999' } : null),
                      }}
                    />
                    <input
                      type="text" value={r.note} disabled={locked}
                      placeholder="Note (optional)"
                      onChange={e => patchRow(r._k, { note: e.target.value })}
                      style={{ ...sx.input, flex: '1 1 180px', minWidth: 140 }}
                    />
                    {!locked && (
                      <button
                        className="btn btn-sm" title="Remove this row"
                        onClick={() => delRow(r._k)}
                        style={{ flex: '0 0 auto', color: '#a12622' }}
                      >×</button>
                    )}
                    {prob && !noCodes && <span style={{ fontSize: 11, color: '#a12622' }}>{prob}</span>}

                    {/* The dead-end case gets a full-width sentence, not a
                        validation chip: there is nothing the PM can do in this
                        row to fix it. The job dropdown and the × stay live so
                        he can switch jobs or drop the row and carry on. */}
                    {noCodes && (
                      <div style={{
                        flex: '1 1 100%', marginTop: 2, fontSize: 11.5, lineHeight: 1.45,
                        color: '#92510a', background: '#fff8f0',
                        border: '1px solid #f0d9bd', borderRadius: 6, padding: '5px 8px',
                      }}>
                        No cost codes are set up for this job yet — ask Carla to add them in
                        Plexxis and they’ll appear here after the next sync. Until then pick a
                        different job or remove this row; the week can’t be saved with it.
                      </div>
                    )}
                    {nonWorked && (
                      <div style={{ flex: '1 1 100%', marginTop: 2, fontSize: 11.5, color: '#3d5468' }}>
                        Vacation / holiday — no job or cost code needed.
                      </div>
                    )}
                  </div>
                )
              })}

              {!locked && (
                <button className="btn btn-sm" onClick={() => addRow(d)} style={{ marginTop: 2 }}>
                  + Add time
                </button>
              )}
              {locked && mine.length === 0 && reps.length === 0 && (
                <div style={{ fontSize: 12, color: '#bbb' }}>No hours.</div>
              )}
            </div>
          </div>
        )
      })}

      {/* Owner / admin / road-super status strip */}
      {canOversee && (
        <div style={{ marginTop: 20, border: '1px solid #e3e7ec', borderRadius: 8, background: '#fff' }}>
          <div style={{ padding: '8px 12px', background: '#f7f9fb', borderBottom: '1px solid #e3e7ec', fontSize: 13, fontWeight: 700, color: '#1e2d3d' }}>
            PM timesheets — week ending {niceDate(weekEnd)}
          </div>
          <div style={{ padding: '8px 12px' }}>
            {overview === null && <div style={{ fontSize: 12, color: '#888' }}>Loading…</div>}
            {overview && overview.length === 0 && (
              <div style={{ fontSize: 12, color: '#888' }}>
                No PM or owner logins are linked to an employee record yet — link them on the Users tab.
              </div>
            )}
            {(overview || []).map(p => (
              <div key={p.employee_id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0',
                borderBottom: '1px solid #f1f4f7', fontSize: 12.5,
              }}>
                <span style={{ minWidth: 180, fontWeight: 600, color: '#1e2d3d' }}>{p.name}</span>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
                  background: p.submitted ? '#eaf6ec' : '#fff8e1',
                  color: p.submitted ? '#1e5128' : '#8a5d00',
                }}>
                  {p.submitted ? 'Submitted' : 'Not submitted'}
                </span>
                <span style={{ color: '#43525f' }}>{p.total_hours} h</span>
                {p.submitted && <span style={{ color: '#888', fontSize: 11 }}>{fmtWhen(p.submitted_at)}</span>}
                {p.submitted && (
                  <button className="btn btn-sm" disabled={busy} style={{ marginLeft: 'auto' }}
                    onClick={() => handleUnlock(p.employee_id, p.name)}>
                    Unlock
                  </button>
                )}
              </div>
            ))}
            <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
              Hours shown are each person’s own timesheet entries. Field-report hours reach payroll
              through the report itself and are not counted here.
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
